import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { WISHLIST_WIDGET_NAMES } from "@/constants/widget-names";
import {
    customerInfoProviderContextToken,
    urlsBuilderContextToken,
} from "@/context_tokens";
import {
    customerInfoProviderToken,
    growaveConfigurationToken,
    modalManagerToken,
} from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { ShareWishlistsQuery } from "@apps/wishlist/queries/ShareWishlistsQuery";
import { WishlistProductStatusesQuery } from "@apps/wishlist/queries/WishlistProductStatusesQuery";
import WishlistsBackInStockItemsQuery from "@apps/wishlist/queries/WishlistsBackInStockItemsQuery";
import { ShareWishlistsService } from "@apps/wishlist/services/ShareWishlistsService";
import WishlistGuestInfoService from "@apps/wishlist/services/WishlistGuestInfoService";
import { WishlistProductStatusesService } from "@apps/wishlist/services/WishlistProductStatusesService";
import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
import WishlistsStorefrontValuesService from "@apps/wishlist/services/WishlistsStorefrontValuesService";
import { CustomerInfoProviderInterface } from "@lib/CustomerInfoProvider/CustomerInfoProviderInterface";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { CartModule } from "@modules/cart/CartModule";
import { CartQuery } from "@modules/cart/queries/CartQuery";
import { CartService } from "@modules/cart/services/CartService";
import CartAvailabilityProvider from "@modules/common_module/CartAvailabilityProvider/CartAvailabilityProvider";
import { CommonModule } from "@modules/common_module/CommonModule";
import { DesignModeProviderInterface } from "@modules/common_module/DesignModeProvider/DesignModeProviderInterface";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import { I18nModule } from "@modules/i18n/I18nModule";
import { ProductsModule } from "@modules/products/ProductsModule";
import { CombinedProductsAndProductsWithQuantityRulesQuery } from "@modules/products/queries/CombinedProductsAndProductsWithQuantityRulesQuery";
import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
import { ProductsWithQuantityRulesQuery } from "@modules/products/queries/ProductsWithQuantityRulesQuery";
import { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
import {
    productsQueryToken,
    productsServiceToken,
    productsWithQuantityRulesQueryToken,
} from "@modules/products/tokens";
import { container, injectable } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import SharedWishlistPageWidgetComponent from "./SharedWishlistPageWidget.svelte";
import { SharedWishlistPageWidgetConfigInterface } from "./SharedWishlistPageWidgetConfig";

@injectable()
class SharedWishlistPageWidget implements WidgetInterface {
    private shareWishlistsService: ShareWishlistsService;
    private shareWishlistsQuery: ShareWishlistsQuery;
    private productsService: ProductsServiceInterface;
    private wishlistsBackInStockItemsQuery: WishlistsBackInStockItemsQuery;
    private wishlistProductStatusesQuery: WishlistProductStatusesQuery;
    private productsQuery: ProductsQuery;
    private combinedProductsAndProductsWithQuantityRulesQuery: CombinedProductsAndProductsWithQuantityRulesQuery;
    private productsWithQuantityRulesQuery: ProductsWithQuantityRulesQuery;
    private cartService: CartService;
    private cartQuery: CartQuery;
    private cartAvailabilityProvider: CartAvailabilityProvider;
    private designModeProvider: DesignModeProviderInterface;
    private wishlistsStorefrontValuesService: WishlistsStorefrontValuesService;
    private customerInfoProvider: CustomerInfoProviderInterface;

    constructor(
        private readonly config: SharedWishlistPageWidgetConfigInterface,
        private readonly logger: LoggerInterface,
        private readonly dynamicImportManager: DynamicImportManager
    ) {
        this.logger.debug("SharedWishlistPageWidget is initialized", config);

        const i18nModule = container.resolve(I18nModule);
        i18nModule.exports.i18n.registerLoader(
            "wishlist.SharedWishlistPageWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "wishlist.SharedWishlistPageWidget"
            )
        );
        i18nModule.exports.i18n.registerLoader(
            "wishlist.critical.SharedWishlistPageWidget",
            i18nModule.exports.i18nCriticalLoaderFactory.getLoader(
                "wishlist.critical.SharedWishlistPageWidget"
            )
        );

        this.shareWishlistsService = container.resolve(ShareWishlistsService);
        this.shareWishlistsQuery = container.resolve(ShareWishlistsQuery);
        this.wishlistsBackInStockItemsQuery = container.resolve(
            WishlistsBackInStockItemsQuery
        );
        this.wishlistProductStatusesQuery = container.resolve(
            WishlistProductStatusesQuery
        );
        this.productsService = container.resolve(productsServiceToken);
        this.productsQuery = container.resolve(productsQueryToken);
        this.combinedProductsAndProductsWithQuantityRulesQuery =
            container.resolve(
                ProductsModule
            ).exports.combinedProductsAndProductsWithQuantityRulesQuery;

        this.productsWithQuantityRulesQuery = container.resolve(
            productsWithQuantityRulesQueryToken
        );
        this.cartService = container.resolve(CartModule).exports.cartService;
        this.cartQuery = container.resolve(CartModule).exports.cartQuery;
        this.cartAvailabilityProvider =
            container.resolve(CommonModule).exports.cartAvailabilityProvider;
        this.designModeProvider =
            container.resolve(CommonModule).exports.designModeProvider;
        this.wishlistsStorefrontValuesService = container.resolve(
            WishlistsStorefrontValuesService
        );
        this.customerInfoProvider = container.resolve(
            customerInfoProviderToken
        );
    }

    async init() {
        this.productsService.subscribe();
        const customizationManager = container.resolve(CustomizationManager);
        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );
        const authenticationInfoProvider =
            container.resolve(AuthenticationModule).exports
                .authenticationInfoProvider;
        const modalManager = container.resolve(modalManagerToken);

        const wishlistsService = container.resolve(WishlistsService);
        const wishlistProductStatusesService = container.resolve(
            WishlistProductStatusesService
        );
        wishlistProductStatusesService.subscribe();
        const wishlistGuestInfoService = container.resolve(
            WishlistGuestInfoService
        );

        const element = this.config.target;
        element.style.display = "block";
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const customizationManagerConfig = {
            widgetName: WISHLIST_WIDGET_NAMES.SHARED_WISHLIST_PAGE_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromBlockCustomCss({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        const contextMap = new Map();
        contextMap.set(
            urlsBuilderContextToken,
            container.resolve(CommonModule).exports.urlsBuilder
        );

        contextMap.set(
            customerInfoProviderContextToken,
            this.customerInfoProvider
        );

        new SharedWishlistPageWidgetComponent({
            target: shadowRoot || element,
            props: {
                productsQuery: this.productsQuery,
                combinedProductsAndProductsWithQuantityRulesQuery:
                    this.combinedProductsAndProductsWithQuantityRulesQuery,
                productsWithQuantityRulesQuery:
                    this.productsWithQuantityRulesQuery,
                shareWishlistsQuery: this.shareWishlistsQuery,
                shareWishlistsService: this.shareWishlistsService,
                wishlistsService: wishlistsService,
                wishlistsBackInStockItemsQuery:
                    this.wishlistsBackInStockItemsQuery,
                wishlistProductStatusesService: wishlistProductStatusesService,
                wishlistProductStatusesQuery: this.wishlistProductStatusesQuery,
                cartService: this.cartService,
                cartQuery: this.cartQuery,
                dynamicImportManager: this.dynamicImportManager,
                cartAvailabilityProvider: this.cartAvailabilityProvider,
                designModeProvider: this.designModeProvider,
                wishlistsStorefrontValuesService:
                    this.wishlistsStorefrontValuesService,
                authenticationInfoProvider: authenticationInfoProvider,
                modalManager: modalManager,
                wishlistGuestInfoService: wishlistGuestInfoService,
                afterWidgetInject: () => {
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    );
                },
            },
            context: contextMap,
        });

        return this;
    }
}

export default SharedWishlistPageWidget;
